/**
 * Sistema de Gates de Fluxo - CIPA Fácil
 *
 * Controla o acesso às funcionalidades baseado na sequência obrigatória do fluxo NR-05:
 * Empresa → Mandato → Membros → Cronograma → Reuniões → Ata → Relatórios
 *
 * @author CIPA Fácil Team
 * @date 2026-02-02
 */

/**
 * Etapas do fluxo NR-05
 */
export type FlowStep =
  | "empresa"
  | "mandato"
  | "membros"
  | "cronograma"
  | "cronograma-planejamento"
  | "reunioes"
  | "ata"
  | "relatorios"
  | "plano-trabalho"
  | "votacao"
  | "documentos"
  | "export";

/**
 * Tipos de plano disponíveis
 *
 * STARTER: até 3 empresas, NR-05 simplificada
 * PROFESSIONAL: até 10 empresas, NR-05 completa + NR-31/CIPATR
 * ENTERPRISE: ilimitado, NR-05 + NR-31 + NR-22/CIPAMIN + pacote total
 * SUPER_ADMIN: acesso total (interno)
 */
export type PlanoUsuario =
  | "STARTER"
  | "PROFESSIONAL"
  | "ENTERPRISE"
  | "SUPER_ADMIN";

/**
 * Status da assinatura
 */
export type StatusAssinatura =
  | "active"
  | "canceled"
  | "suspended"
  | "expired"
  | "pending";

/**
 * Contexto necessário para validar acesso às funcionalidades
 */
export interface FlowContext {
  /** ID da empresa selecionada (se houver) */
  empresaSelecionada?: string;

  /** Se há mandato ativo para a empresa */
  temMandatoAtivo: boolean;

  /** ID do mandato ativo */
  mandatoAtivoId?: string;

  /** Total de membros cadastrados no mandato ativo */
  totalMembros: number;

  /** Plano atual do usuário */
  planoUsuario: PlanoUsuario;

  /** Status da assinatura */
  statusAssinatura: StatusAssinatura;

  /** Tipo de usuário (legado, para compatibilidade) */
  tipoUsuario?: string;

  /** Se há reuniões no cronograma */
  temReunioes: boolean;

  /** Se há atas criadas */
  temAtas: boolean;

  /** Módulos habilitados para o plano */
  modulosHabilitados?: {
    nr31: boolean; // CIPATR
    nr22: boolean; // CIPAMIN
  };

  /** Tipo de comissão da empresa selecionada */
  tipoComissao?: "CIPA" | "CIPATR" | "CIPAMIN";
}

/**
 * Resultado da verificação de acesso
 */
export interface GateResult {
  /** Se o acesso é permitido */
  allowed: boolean;

  /** Motivo do bloqueio (quando allowed = false) */
  reason?: string;

  /** Texto do botão de ação (CTA) */
  ctaLabel?: string;

  /** Rota para redirecionamento (uso externo como /planos) */
  ctaRoute?: string;

  /** ID da aba para navegação interna */
  ctaTab?: string;

  /** Ícone sugerido para o bloqueio */
  icon?: "lock" | "warning" | "info" | "upgrade";

  /** Tipo de bloqueio */
  blockType?: "flow" | "plan" | "permission";
}

/**
 * Configuração de requisitos por etapa
 */
interface StepRequirements {
  requiresEmpresa: boolean;
  requiresMandato: boolean;
  requiresMembros: boolean;
  requiresReunioes: boolean;
  minMembros: number;
  allowedPlans: PlanoUsuario[];
  requiresModulo?: {
    nr31?: boolean; // Requer módulo CIPATR/NR-31
    nr22?: boolean; // Requer módulo CIPAMIN/NR-22
  };
}

/**
 * Mapa de requisitos por etapa do fluxo
 *
 * Planos válidos (conforme Plano Kamikaze):
 * - STARTER: até 3 empresas, NR-05 simplificada, sem módulos avançados
 * - PROFESSIONAL: até 10 empresas, NR-05 completa + NR-31/CIPATR habilitado
 * - ENTERPRISE: ilimitado, NR-05 + NR-31 + NR-22/CIPAMIN + pacote completo
 * - SUPER_ADMIN: acesso total
 *
 * Módulos por plano:
 * - NR-31/CIPATR: disponível apenas PROFESSIONAL e ENTERPRISE
 * - NR-22/CIPAMIN: disponível apenas ENTERPRISE
 */
const STEP_REQUIREMENTS: Record<FlowStep, StepRequirements> = {
  empresa: {
    requiresEmpresa: false,
    requiresMandato: false,
    requiresMembros: false,
    requiresReunioes: false,
    minMembros: 0,
    allowedPlans: ["STARTER", "PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"],
  },
  mandato: {
    requiresEmpresa: true,
    requiresMandato: false,
    requiresMembros: false,
    requiresReunioes: false,
    minMembros: 0,
    allowedPlans: ["STARTER", "PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"],
  },
  membros: {
    requiresEmpresa: true,
    requiresMandato: true,
    requiresMembros: false,
    requiresReunioes: false,
    minMembros: 0,
    allowedPlans: ["STARTER", "PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"],
  },
  "cronograma-planejamento": {
    requiresEmpresa: true,
    requiresMandato: false, // ⚠️ KEY: Não exige mandato
    requiresMembros: false,
    requiresReunioes: false,
    minMembros: 0,
    allowedPlans: ["STARTER", "PROFESSIONAL", "SUPER_ADMIN"],
  },
  cronograma: {
    requiresEmpresa: true,
    requiresMandato: true, // ⚠️ Mantém exigência de mandato
    requiresMembros: true,
    requiresReunioes: false,
    minMembros: 2,
    allowedPlans: ["ENTERPRISE", "SUPER_ADMIN"], // ⚠️ Apenas ENTERPRISE
  },
  reunioes: {
    requiresEmpresa: true,
    requiresMandato: true,
    requiresMembros: false, // ⚠️ Membros não obrigatórios para STARTER/PROFESSIONAL
    requiresReunioes: false,
    minMembros: 0, // ⚠️ ENTERPRISE pode validar separadamente
    allowedPlans: ["STARTER", "PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"],
  },
  ata: {
    requiresEmpresa: true,
    requiresMandato: true,
    requiresMembros: false, // ⚠️ Membros não obrigatórios para STARTER/PROFESSIONAL
    requiresReunioes: true,
    minMembros: 0, // ⚠️ ENTERPRISE pode validar separadamente
    allowedPlans: ["STARTER", "PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"],
  },
  "plano-trabalho": {
    requiresEmpresa: true,
    requiresMandato: true,
    requiresMembros: false,
    requiresReunioes: false,
    minMembros: 0,
    // Disponível para todos os planos com mandato ativo
    allowedPlans: ["STARTER", "PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"],
  },
  votacao: {
    requiresEmpresa: true,
    requiresMandato: true,
    requiresMembros: true,
    requiresReunioes: false,
    minMembros: 2,
    allowedPlans: ["ENTERPRISE", "SUPER_ADMIN"], // Apenas Enterprise+
  },
  relatorios: {
    requiresEmpresa: true,
    requiresMandato: true,
    requiresMembros: false, // ⚠️ Membros não obrigatórios para STARTER/PROFESSIONAL
    requiresReunioes: false,
    minMembros: 0, // ⚠️ ENTERPRISE pode validar separadamente

    allowedPlans: ["STARTER", "PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"],
  },
  documentos: {
    requiresEmpresa: true,
    requiresMandato: true,
    requiresMembros: false,
    requiresReunioes: false,
    minMembros: 0,
    allowedPlans: ["STARTER", "PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"],
  },
  export: {
    // Modelos e templates são documentos estáticos — não dependem de empresa
    // selecionada nem de mandato ativo. O gate é puramente de plano.
    requiresEmpresa: false,
    requiresMandato: false,
    requiresMembros: false,
    requiresReunioes: false,
    minMembros: 0,
    allowedPlans: ["PROFESSIONAL", "ENTERPRISE", "SUPER_ADMIN"], // Export avançado apenas Professional+
  },
};

/**
 * Verifica se o usuário pode acessar uma etapa específica do fluxo.
 *
 * @param step - Etapa do fluxo a verificar
 * @param context - Contexto atual do usuário
 * @returns Resultado da verificação com motivo e ação sugerida
 *
 * @example
 * ```typescript
 * const result = canAccess('cronograma', {
 *   empresaSelecionada: 'abc-123',
 *   temMandatoAtivo: true,
 *   totalMembros: 5,
 *   planoUsuario: 'PROFESSIONAL',
 *   statusAssinatura: 'active',
 *   temReunioes: false,
 *   temAtas: false
 * });
 *
 * if (!result.allowed) {
 *   showBlockedMessage(result.reason, result.ctaLabel, result.ctaRoute);
 * }
 * ```
 */
export function canAccess(step: FlowStep, context: FlowContext): GateResult {
  const requirements = STEP_REQUIREMENTS[step];

  if (!requirements) {
    console.warn(`[FlowGates] Etapa desconhecida: ${step}`);
    return { allowed: true };
  }

  // 1. Verificar se empresa está selecionada
  if (requirements.requiresEmpresa && !context.empresaSelecionada) {
    return {
      allowed: false,
      reason: "Selecione uma empresa para continuar.",
      ctaLabel: "Ir para Empresas",
      ctaTab: "empresas",
      icon: "warning",
      blockType: "flow",
    };
  }

  // 2. Verificar se tem mandato ativo
  if (requirements.requiresMandato && !context.temMandatoAtivo) {
    return {
      allowed: false,
      reason:
        "Nenhum mandato ativo encontrado. Crie um mandato para a CIPA antes de continuar.",
      ctaLabel: "Criar Mandato",
      ctaTab: "gestao-mandatos",
      icon: "warning",
      blockType: "flow",
    };
  }

  // 3. Verificar se tem membros suficientes
  if (
    requirements.requiresMembros &&
    context.totalMembros < requirements.minMembros
  ) {
    const faltam = requirements.minMembros - context.totalMembros;
    return {
      allowed: false,
      reason: `Cadastre pelo menos ${requirements.minMembros} membros da CIPA para continuar. Faltam ${faltam} membro(s).`,
      ctaLabel: "Cadastrar Membros",
      ctaTab: "gestao-membros",
      icon: "warning",
      blockType: "flow",
    };
  }

  // 4. Verificar se tem reuniões no cronograma
  if (requirements.requiresReunioes && !context.temReunioes) {
    return {
      allowed: false,
      reason: "Gere o cronograma de reuniões antes de criar atas.",
      ctaLabel: "Ir para Cronograma",
      ctaTab: "cronograma-reunioes",
      icon: "info",
      blockType: "flow",
    };
  }

  // 5. Verificar módulos habilitados (NR-31/CIPATR, NR-22/CIPAMIN)
  if (requirements.requiresModulo?.nr31 && !context.modulosHabilitados?.nr31) {
    return {
      allowed: false,
      reason:
        "Esta funcionalidade requer o módulo CIPATR/NR-31, disponível nos planos Professional e Enterprise.",
      ctaLabel: "Fazer Upgrade",
      ctaRoute: "/planos",
      icon: "upgrade",
      blockType: "plan",
    };
  }

  if (requirements.requiresModulo?.nr22 && !context.modulosHabilitados?.nr22) {
    return {
      allowed: false,
      reason:
        "Esta funcionalidade requer o módulo CIPAMIN/NR-22, disponível apenas no plano Enterprise.",
      ctaLabel: "Fazer Upgrade",
      ctaRoute: "/planos",
      icon: "upgrade",
      blockType: "plan",
    };
  }

  // 6. Verificar plano - Bloqueio por funcionalidade
  if (!requirements.allowedPlans.includes(context.planoUsuario)) {
    const isPlanFeature = ["plano-trabalho", "relatorios"].includes(step);

    return {
      allowed: false,
      reason: isPlanFeature
        ? `Esta funcionalidade está disponível apenas para planos ${requirements.allowedPlans.filter((p) => p !== "SUPER_ADMIN").join(", ")}.`
        : "Seu plano não tem acesso a esta funcionalidade.",
      ctaLabel: "Fazer Upgrade",
      ctaRoute: "/planos",
      icon: "upgrade",
      blockType: "plan",
    };
  }

  // Tudo OK!
  return { allowed: true };
}

/**
 * Verifica se o usuário está no plano STARTER
 * @param context - Contexto do fluxo
 * @returns true se estiver no STARTER
 */
export function isStarterPlan(context: FlowContext): boolean {
  return context.planoUsuario === "STARTER";
}

/**
 * Verifica se o usuário pode exportar documentos
 * @param context - Contexto do fluxo
 * @returns true se pode exportar
 */
export function canExport(context: FlowContext): boolean {
  const result = canAccess("export", context);
  return result.allowed;
}

/**
 * Retorna os módulos habilitados para um plano
 * @param plano - Plano do usuário
 * @returns Objeto com módulos habilitados
 */
export function getModulosHabilitados(plano: PlanoUsuario): {
  nr31: boolean;
  nr22: boolean;
} {
  switch (plano) {
    case "STARTER":
      return { nr31: false, nr22: false };
    case "PROFESSIONAL":
      return { nr31: true, nr22: false };
    case "ENTERPRISE":
    case "SUPER_ADMIN":
      return { nr31: true, nr22: true };
    default:
      return { nr31: false, nr22: false };
  }
}

/**
 * Verifica se o plano tem permissão para usar o CTA de navegação dinâmica
 * Apenas ENTERPRISE e SUPER_ADMIN podem usar (tem acesso a todas as tabs)
 * @param plano - Plano do usuário
 * @returns true se pode usar CTA de navegação
 */
export function canUseNavigationCTA(plano: PlanoUsuario): boolean {
  return plano === "ENTERPRISE" || plano === "SUPER_ADMIN";
}

/**
 * Retorna lista de etapas bloqueadas para o contexto atual
 * @param context - Contexto do fluxo
 * @returns Lista de etapas bloqueadas
 */
export function getBlockedSteps(context: FlowContext): FlowStep[] {
  const steps: FlowStep[] = [
    "empresa",
    "mandato",
    "membros",
    "cronograma",
    "reunioes",
    "ata",
    "plano-trabalho",
    "relatorios",
    "documentos",
    "export",
  ];

  return steps.filter((step) => !canAccess(step, context).allowed);
}

/**
 * Retorna a próxima etapa recomendada do fluxo
 * @param context - Contexto do fluxo
 * @returns Próxima etapa ou null se fluxo completo
 */
export function getNextRecommendedStep(context: FlowContext): FlowStep | null {
  if (!context.empresaSelecionada) return "empresa";
  if (!context.temMandatoAtivo) return "mandato";
  if (context.totalMembros < 2) return "membros";
  if (!context.temReunioes) return "cronograma";
  if (!context.temAtas) return "ata";
  return null;
}

/**
 * Mensagens padrão por tipo de bloqueio
 */
export const BLOCK_MESSAGES = {
  flow: {
    title: "Etapa do Fluxo",
    description: "Complete as etapas anteriores para desbloquear.",
  },
  plan: {
    title: "Plano Necessário",
    description: "Faça upgrade do seu plano para acessar.",
  },
  permission: {
    title: "Sem Permissão",
    description: "Você não tem permissão para acessar.",
  },
};

const flowGates = {
  canAccess,
  canExport,
  getBlockedSteps,
  getNextRecommendedStep,
  getModulosHabilitados,
  BLOCK_MESSAGES,
};

export default flowGates;
